import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,
});

// JWT user token (email/password). Google sessions use cookie.
function getUserToken() {
  return localStorage.getItem("amd64_user_token") || "";
}
export function setUserToken(t) {
  if (t) localStorage.setItem("amd64_user_token", t);
  else localStorage.removeItem("amd64_user_token");
}

export function getAdminToken() {
  return localStorage.getItem("amd64_admin_token") || "";
}
export function setAdminToken(t) {
  if (t) localStorage.setItem("amd64_admin_token", t);
  else localStorage.removeItem("amd64_admin_token");
}

api.interceptors.request.use((config) => {
  // Admin endpoints use admin token; user endpoints use user token. Both via Authorization header.
  const url = config.url || "";
  const isAdmin = url.startsWith("/admin") || url.includes("/admin/");
  const token = isAdmin ? getAdminToken() : getUserToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============ Public ============
export const fetchCategories = () => api.get("/categories").then((r) => r.data);
export const fetchSettings = () => api.get("/settings/public").then((r) => r.data);
export const fetchListings = (params = {}) => api.get("/listings", { params }).then((r) => r.data);
export const fetchListing = (id) => api.get(`/listings/${id}`).then((r) => r.data);
export const fetchPublicProfile = (uid) => api.get(`/users/${uid}/public`).then((r) => r.data);

// ============ Auth ============
export const authRegister = (body) => api.post("/auth/register", body).then((r) => r.data);
export const authLogin = (body) => api.post("/auth/login", body).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authUpdateMe = (body) => api.patch("/auth/me", body).then((r) => r.data);
export const authGoogleSession = (sessionId) =>
  api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);

// ============ User listings & favorites ============
export const myListings = (status) => api.get("/listings/mine", { params: status ? { status } : {} }).then((r) => r.data);
export const createListing = (body) => api.post("/listings", body).then((r) => r.data);
export const updateListing = (id, body) => api.patch(`/listings/${id}`, body).then((r) => r.data);
export const deleteListing = (id) => api.delete(`/listings/${id}`).then((r) => r.data);
export const markSold = (id) => api.post(`/listings/${id}/sold`).then((r) => r.data);

export const getFavorites = () => api.get("/favorites").then((r) => r.data);
export const addFavorite = (id) => api.post(`/favorites/${id}`).then((r) => r.data);
export const removeFavorite = (id) => api.delete(`/favorites/${id}`).then((r) => r.data);

export const uploadImage = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api.post("/images", form, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ============ Admin ============
export const adminLoginApi = (body) => api.post("/admin/login", body).then((r) => r.data);
export const adminMe = () => api.get("/admin/me").then((r) => r.data);
export const adminGetSettings = () => api.get("/admin/settings").then((r) => r.data);
export const adminPatchSettings = (body) => api.patch("/admin/settings", body).then((r) => r.data);
export const adminListCategories = () => api.get("/admin/categories").then((r) => r.data);
export const adminCreateCategory = (body) => api.post("/admin/categories", body).then((r) => r.data);
export const adminUpdateCategory = (id, body) => api.patch(`/admin/categories/${id}`, body).then((r) => r.data);
export const adminDeleteCategory = (id) => api.delete(`/admin/categories/${id}`).then((r) => r.data);
export const adminListListings = (status) => api.get("/admin/listings", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateListingStatus = (id, status) => api.patch(`/admin/listings/${id}/status`, { status }).then((r) => r.data);
export const adminFeatureListing = (id, featured) => api.post(`/admin/listings/${id}/feature`, { featured }).then((r) => r.data);
export const adminDeleteListing = (id) => api.delete(`/admin/listings/${id}`).then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
